// ===========================================
// Edit the next action (enquiries plan §5.7)
// ===========================================
// Owner, the action and its due day. "Back to the group" sends `ownerUserId: null` (the server
// returns the owner to the group the stage's owner came from). The due day is a `YYYY-MM-DD` key,
// not before the SCHOOL's today (`config.schoolToday`).

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import type { Enquiry } from '../../../api/enquiry.api';
import { apiErrorMessage, useAssignableOwners, useEnquiryConfig, useSetNextAction } from '../../../hooks/useEnquiries';
import { dayKey } from './enquiryUi';

export interface NextActionDialogProps {
  enquiry: Enquiry;
  open: boolean;
  onClose: () => void;
}

const GROUP = '';
const MAX_ACTION = 200;

export default function NextActionDialog({ enquiry, open, onClose }: NextActionDialogProps) {
  const { data: config } = useEnquiryConfig();
  const { data: owners, isLoading: ownersLoading, isError: ownersError } = useAssignableOwners(open);
  const setNextAction = useSetNextAction({ quietErrors: true });

  const [ownerId, setOwnerId] = useState<string>(GROUP);
  const [action, setAction] = useState('');
  const [due, setDue] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Pre-fill from the enquiry each time the dialog opens (not on every refetch while it is open).
  useEffect(() => {
    if (!open) return;
    setOwnerId(enquiry.assignedToId ?? GROUP);
    setAction(enquiry.nextAction ?? '');
    setDue(dayKey(enquiry.nextActionDueDate) ?? '');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, enquiry.id]);

  const ownerList = Array.isArray(owners) ? owners : [];
  const ownerOptions: Array<{ id: string; name: string }> = ownerList.map((o) => ({
    id: o.id,
    name: `${o.firstName} ${o.lastName}`,
  }));
  // The current owner may not be pickable (a Meet & Greet teacher never holds enquiries.read, and an
  // owner who lost access stays the owner): show them by name, or the select would read "Back to
  // the group" while Save keeps them (review 5.7 M3).
  if (enquiry.assignedToId && !ownerOptions.some((o) => o.id === enquiry.assignedToId)) {
    const who = enquiry.AssignedTo ? `${enquiry.AssignedTo.firstName} ${enquiry.AssignedTo.lastName}` : 'Current owner';
    ownerOptions.push({ id: enquiry.assignedToId, name: `${who} (current owner)` });
  }
  const minDue = config?.schoolToday;
  const duePast = !!due && !!minDue && due < minDue;
  const valid = action.trim().length > 0 && action.length <= MAX_ACTION && !!due && !duePast;

  const handleSave = () => {
    if (!valid) return;
    setError(null);
    setNextAction.mutate(
      {
        id: enquiry.id,
        data: {
          ownerUserId: ownerId === GROUP ? null : ownerId,
          nextAction: action.trim(),
          nextActionDueDate: due,
        },
      },
      {
        onSuccess: () => onClose(),
        onError: (e) => setError(apiErrorMessage(e, 'Failed to save the next action')),
      },
    );
  };

  return (
    <Dialog open={open} onClose={setNextAction.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Next action</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {ownersError && <Alert severity="warning">Could not load the list of people.</Alert>}
          <FormControl fullWidth size="small" disabled={ownersLoading}>
            <InputLabel id="next-action-owner-label">Owner</InputLabel>
            <Select
              labelId="next-action-owner-label"
              label="Owner"
              value={ownerOptions.some((o) => o.id === ownerId) ? ownerId : GROUP}
              onChange={(e) => setOwnerId(String(e.target.value))}
            >
              <MenuItem value={GROUP}>Back to the group</MenuItem>
              {ownerOptions.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Next action"
            required
            fullWidth
            size="small"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            inputProps={{ maxLength: MAX_ACTION }}
          />
          <TextField
            label="Due date"
            type="date"
            required
            fullWidth
            size="small"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minDue }}
            error={duePast}
            helperText={duePast ? 'The due date has passed.' : undefined}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={setNextAction.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!valid || setNextAction.isPending}>
          {setNextAction.isPending ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
